import { UseMutationOptions, useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { SessionState, useAuthProvider } from '@askporter/auth-provider';
import { ErrorModel, UserReadExternal, UserUpdateExternal } from '@askporter/client-grieg-lyric';
import { setCacheBuster, useTranslation } from '@askporter/config-provider';
import { API } from '../utils';

/** Setting the language has many facets, so this centralises language setting  */
function useSetLanguage(): {
  setLanguage: ({
    languageCode,
    profileMutationOpts,
  }?: {
    languageCode?: string;
    profileMutationOpts?: UseMutationOptions;
  }) => void;
  isLanguageConsistent: (profilePreferredLanguage: string) => boolean;
  isLoading: boolean;
} {
  const profileAPIPath = 'profile';
  const queryClient = useQueryClient();
  const isMutatingProfile = useIsMutating({
    mutationKey: ['mutateProfileLanguage'],
  });

  const { i18n } = useTranslation();
  const { sessionState } = useAuthProvider();
  const isAuthenticated = SessionState[sessionState] === 'valid' ? true : false;

  const localStorageLng = () => localStorage.getItem('selectedLanguage');
  const i18nLng = i18n?.language;

  const updateProfile = useMutation<AxiosResponse, AxiosError<ErrorModel>, UserUpdateExternal>({
    mutationKey: ['mutateProfileLanguage'],
    mutationFn: (payload: UserUpdateExternal) => API().post({ path: profileAPIPath, payload }),

    onMutate: async (userProfileUpdate) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: [profileAPIPath] });

      // Snapshot the previous value
      const previousProfileData: UserReadExternal = queryClient.getQueryData([profileAPIPath]);

      // Optimistically update to the new value
      queryClient.setQueryData([profileAPIPath], (old: UserReadExternal) => ({ ...old, ...userProfileUpdate }));

      // Return a context object with the snapshotted value
      return previousProfileData;
    },

    // Always refetch after error or success:
    onSettled: () => queryClient.invalidateQueries({ queryKey: [profileAPIPath] }),
  });

  /** Apply language code consistently, if a language code is not passed the local storage language will be used  */
  const setLanguage = async (
    { languageCode, profileMutationOpts = {} } = { languageCode: localStorageLng(), profileMutationOpts: {} },
  ) => {
    localStorage.setItem('selectedLanguage', languageCode);
    localStorage.setItem('languageChanged', new Date().toJSON());
    setCacheBuster();
    await i18n.changeLanguage(languageCode);

    // Invalidate the static tenant query to ensure the translations are updated to current language
    queryClient.invalidateQueries({ queryKey: ['static/tenant'] });

    isAuthenticated && isMutatingProfile === 0
      ? updateProfile.mutate({ preferredLanguage: languageCode }, profileMutationOpts)
      : null;
  };

  /** Check if the profile language is consistent with local storage and i18n */
  const isLanguageConsistent = (profilePreferredLng: string): boolean => {
    if (profilePreferredLng === localStorageLng() && profilePreferredLng === i18nLng) {
      return true;
    }

    return false;
  };

  return { setLanguage, isLanguageConsistent, isLoading: updateProfile.isPending };
}

export default useSetLanguage;
